import { useState, useMemo } from "react";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  Loader2,
  UserCircle,
  BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { ExportCandidatesModal } from "@/pages/candidates/components/ExportCandidatesModal";
import { useMyProfiles, useDeleteMyProfile } from "./hooks/useMyResumes";
import { useDuplicateCandidate } from "@/pages/candidates/hooks/useCandidate";
import { useCurrentUser } from "@/pages/auth/hooks/useAuth";
import ResumeEditModal from "./components/ResumeEditModal";
import type { MyProfileDetail, MyProfileSkill } from "./api/resume.api";
import { toast } from "@/utils/toast.service";

export default function MyResumes() {
  const [search, setSearch] = useState("");
  const [selectedProfile, setSelectedProfile] =
    useState<MyProfileDetail | null>(null);
  const [profileToDelete, setProfileToDelete] =
    useState<MyProfileDetail | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateConfirmOpen, setIsCreateConfirmOpen] = useState(false);
  const [exportCandidateId, setExportCandidateId] = useState<
    number | string | null
  >(null);

  const { data: profilesResponse, isLoading, refetch } = useMyProfiles();
  const duplicateCandidateMutation = useDuplicateCandidate();
  const deleteProfileMutation = useDeleteMyProfile();
  const { data: currentUser } = useCurrentUser();

  const rawProfilesData = profilesResponse?.data;
  const profiles: MyProfileDetail[] = useMemo(() => {
    if (Array.isArray(rawProfilesData)) return rawProfilesData;
    if (rawProfilesData && Array.isArray((rawProfilesData as any).results)) {
      return (rawProfilesData as any).results;
    }
    return [];
  }, [rawProfilesData]);

  const filteredProfiles = useMemo(() => {
    if (!search.trim()) return profiles;
    const query = search.toLowerCase().trim();
    return profiles.filter((p) => {
      const name = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
      const role = (p.job_role || "").toLowerCase();
      const skillsText = Array.isArray(p.skills)
        ? p.skills
          .map((s) =>
            typeof s === "object" && s !== null
              ? (s as MyProfileSkill).skill_name || ""
              : String(s),
          )
          .join(" ")
          .toLowerCase()
        : "";
      return (
        name.includes(query) ||
        role.includes(query) ||
        skillsText.includes(query)
      );
    });
  }, [profiles, search]);

  const handleCreateNewProfile = async () => {
    const targetId = profiles.length > 0 ? profiles[0].id : currentUser?.id;
    if (!targetId) {
      toast.error("No candidate profile available to duplicate.");
      setIsCreateConfirmOpen(false);
      return;
    }

    try {
      await duplicateCandidateMutation.mutateAsync(targetId);
      setIsCreateConfirmOpen(false);
      refetch();
    } catch (err: any) {
      toast.error(err?.message || "Failed to create new profile.");
    }
  };

  const handleEditClick = (profile: MyProfileDetail) => {
    setSelectedProfile(profile);
    setIsEditModalOpen(true);
  };

  const handleDeleteProfile = async () => {
    if (!profileToDelete?.id) return;
    try {
      await deleteProfileMutation.mutateAsync(profileToDelete.id);
      if (selectedProfile?.id === profileToDelete.id) {
        setIsEditModalOpen(false);
        setSelectedProfile(null);
      }
      setProfileToDelete(null);
      refetch();
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete profile.");
    }
  };

  return (
    <div className="w-full max-w-[1920px] mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            My Resumes & Profiles
          </h1>
          <p className="text-sm text-slate-500">
            Manage, edit, and create your targeted profiles.
          </p>
        </div>
        <Button
          onClick={() => setIsCreateConfirmOpen(true)}
          disabled={duplicateCandidateMutation.isPending}
          className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
        >
          {duplicateCandidateMutation.isPending ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Plus className="w-5 h-5 mr-2" />
          )}
          Create New Profile
        </Button>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProfiles.map((profile) => {
              const fullName =
                `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
                profile.username ||
                "Candidate Profile";
              const jobRole = profile.job_role || "No Job Role Specified";
              const skillsList: string[] = Array.isArray(profile.skills)
                ? profile.skills
                  .map((s) =>
                    typeof s === "object" && s !== null
                      ? (s as MyProfileSkill).skill_name || ""
                      : String(s),
                  )
                  .filter(Boolean)
                : [];

              return (
                <div
                  key={profile.id}
                  className="group border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-md transition-all bg-white dark:bg-slate-900 flex flex-col h-full"
                >
                  {/* Card Header / Preview Section */}
                  <div className="h-44 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 relative p-4 flex flex-col justify-between">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300 flex items-center justify-center font-bold text-sm shrink-0">
                          <UserCircle className="w-4 h-4" />
                        </div>
                        <h4
                          className="font-bold text-slate-900 dark:text-white text-sm truncate"
                          title={fullName}
                        >
                          {fullName}
                        </h4>
                      </div>
                      {Boolean(profile.is_original) && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800 shrink-0 shadow-xs">
                          <BadgeCheck className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                          Original
                        </span>
                      )}
                    </div>

                    <div className="absolute inset-0 bg-purple-900/40 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                      <Button
                        size="icon"
                        onClick={() => setExportCandidateId(profile.id)}
                        className="w-10 h-10 bg-white text-purple-700 rounded-full shadow-lg hover:bg-purple-50"
                        title="Preview & Export Candidate"
                      >
                        <Eye className="w-5 h-5" />
                      </Button>
                      <Button
                        size="icon"
                        onClick={() => handleEditClick(profile)}
                        className="w-10 h-10 bg-white text-purple-700 rounded-full shadow-lg hover:bg-purple-50"
                        title="Edit Profile Details"
                      >
                        <Edit2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>

                  <div className="p-5 flex-1 flex flex-col">
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white text-sm mb-1 line-clamp-1">
                        {jobRole}
                      </h3>
                      <p className="text-xs text-slate-500 mb-4 flex items-center gap-1.5 justify-between">
                        {profile.updated_at
                          ? "Updated at  " +
                          new Date(profile.updated_at).toLocaleDateString()
                          : "Recently"}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                          title="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            setProfileToDelete(profile);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* New Profile Card Action */}
            <Button
              variant="outline"
              disabled={duplicateCandidateMutation.isPending}
              onClick={() => setIsCreateConfirmOpen(true)}
              className="border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl h-full min-h-[320px] flex flex-col items-center justify-center text-slate-500 hover:text-purple-600 hover:border-purple-300 hover:bg-purple-50/50 transition-all group"
            >
              <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 group-hover:bg-purple-100 flex items-center justify-center mb-4 transition-colors">
                {duplicateCandidateMutation.isPending ? (
                  <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                ) : (
                  <Plus className="w-6 h-6" />
                )}
              </div>
              <span className="font-bold text-sm">Create New Profile</span>
              <span className="text-xs mt-1">
                Duplicate & customize candidate profile
              </span>
            </Button>
          </div>
        )}
      </div>

      {/* Confirmation Dialog before Creating New Profile */}
      <ConfirmationDialog
        isOpen={isCreateConfirmOpen}
        onClose={() => setIsCreateConfirmOpen(false)}
        onConfirm={handleCreateNewProfile}
        title="Create New Profile"
        description="Are you sure you want to create a new profile by duplicating your base candidate profile?"
        confirmText="Create Profile"
        cancelText="Cancel"
        loading={duplicateCandidateMutation.isPending}
      />

      {/* Confirmation Dialog before Deleting Profile */}
      <ConfirmationDialog
        isOpen={profileToDelete !== null}
        onClose={() => setProfileToDelete(null)}
        onConfirm={handleDeleteProfile}
        title="Delete Profile"
        description={`Are you sure you want to delete ${profileToDelete
          ? `"${`${profileToDelete.first_name || ""} ${profileToDelete.last_name || ""}`.trim() || profileToDelete.job_role || profileToDelete.username || "this profile"}"`
          : "this profile"
          }? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={deleteProfileMutation.isPending}
      />

      {/* Edit Profile Tabbed Modal */}
      <ResumeEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedProfile(null);
        }}
        profileId={selectedProfile?.id}
        initialData={selectedProfile}
      />

      {/* Export Candidate Modal */}
      <ExportCandidatesModal
        isOpen={exportCandidateId !== null}
        onClose={() => setExportCandidateId(null)}
        selectedIds={exportCandidateId ? [exportCandidateId] : []}
        filename={`${currentUser?.first_name}_${currentUser?.last_name}`}
      />
    </div>
  );
}
